import { OrbitControls } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import { type JSX } from 'react'
import * as THREE from 'three'

import { useAnimationContext } from '@components/storeMap/floor/3d/AnimationContext'
import BoundsWrapper from '@components/storeMap/floor/3d/BoundsWrapper'
import Lights from '@components/storeMap/floor/3d/Lights'
import SpinningUbicaCube from '@components/storeMap/floor/3d/SpinningUbicaCube'
import TestBridge from '@components/storeMap/floor/3d/TestBridge'
import Bretter from '@components/storeMap/floor/3d/overlay/Bretter'
import CashRegister from '@components/storeMap/floor/3d/overlay/CashRegister'
import CategoryMarkers from '@components/storeMap/floor/3d/overlay/CategoryMarkers'
import FacingStateLayer from '@components/storeMap/floor/3d/overlay/FacingStateLayer'
import FacingsDebug from '@components/storeMap/floor/3d/overlay/FacingsDebug'
import ContextAwareShelf from '@components/storeMap/floor/3d/shelf/ContextAwareShelf'
import ShelfRegistry from '@components/storeMap/floor/ShelfRegistry'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'
import { useLayoutWithMapOrigin } from '@components/storeMap/utils'
import useShouldRotate from '@hooks/useShouldRotate.ts'
import { isDevMode, isIntegrationMode, isTestMode } from '@utils/env'

import { AnimationProvider } from './AnimationProvider'
import AxisLabels3D from './components/AxisLabels3D'

import type { TransformedLayoutResponse } from '@state/layout/types'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'

function ThreeDim() {
  return (
    <AnimationProvider>
      <ThreeDimContent />
    </AnimationProvider>
  )
}

const PANNING_MARGIN = 5
const MIN_DISTANCE = 10
const MAX_DISTANCE = 40

function usePanVectors(layout?: TransformedLayoutResponse): [THREE.Vector3, THREE.Vector3] {
  const width = layout ? layout.width : 20
  const height = layout ? layout.height : 20

  const minPan = new THREE.Vector3(-width / 2 - PANNING_MARGIN, -2, -height / 2 - PANNING_MARGIN)
  const maxPan = new THREE.Vector3(width / 2 + PANNING_MARGIN, 2, height / 2 + PANNING_MARGIN)

  return [minPan, maxPan]
}

function useLayers(injectedOverlays: JSX.Element[] = []): JSX.Element[] {
  const { isLayerActive } = useStoreMapContext()

  const layers = [...injectedOverlays]

  if (isLayerActive('CATEGORY_GROUPING') || isLayerActive('CATEGORY_GROUPING_DEBUG')) {
    layers.push(<CategoryMarkers />)
  }

  if (isLayerActive('HIGHLIGHT_OUT_OF_SHELF')) {
    layers.push(<FacingStateLayer stockLevel="NO_STOCK" />)
  }

  if (isLayerActive('HEATMAP_UNKNOWNS')) {
    layers.push(<FacingStateLayer stockLevel="UNKNOWN" />)
  }

  return layers
}

function ThreeDimContent() {
  const { data: layout } = useLayoutWithMapOrigin()
  const { selectedShelfId, overlays3D, setZoomAtLimit } = useStoreMapContext()
  const { shouldAnimate } = useAnimationContext()
  const shouldRotate = useShouldRotate()

  const [minPan, maxPan] = usePanVectors(layout)
  const layers = useLayers(overlays3D)

  function initOrbitControls(controls: OrbitControlsImpl | null) {
    if (!controls) return

    function handleChange() {
      controls!.target.clamp(minPan, maxPan)
      // The Bounds component can sometimes override these values (e.g. when using the zoom buttons)
      // so we enforce them here
      controls!.minDistance = MIN_DISTANCE
      controls!.maxDistance = MAX_DISTANCE

      const distance = controls!.getDistance()

      if (Math.abs(distance - MIN_DISTANCE) < 0.1) {
        setZoomAtLimit('MAX')
      } else if (Math.abs(distance - MAX_DISTANCE) < 0.1) {
        setZoomAtLimit('MIN')
      } else {
        setZoomAtLimit(undefined)
      }
    }

    controls.addEventListener('change', handleChange)

    return () => {
      controls.removeEventListener('change', handleChange)
    }
  }

  return (
    <Canvas
      camera={{ position: [0, 20, 20], isPerspectiveCamera: true, near: 0.001, far: 1000 }}
      shadows
      frameloop={shouldAnimate ? 'always' : 'demand'}
      dpr={[1, 1.5]}
      data-testid="floor:3d"
    >
      <ShelfRegistry>
        <OrbitControls
          ref={initOrbitControls}
          enableDamping={!isTestMode} // no animations in test mode
          dampingFactor={0.05}
          // minPolarAngle={Math.PI / 8}
          maxPolarAngle={Math.PI / 2}
          makeDefault
        />

        <group rotation={shouldRotate ? [0, Math.PI / 2, 0] : [0, 0, 0]}>
          <Lights />

          <AxisLabels3D />

          {!layout && <Loader />}

          {layout && (
            <>
              <Floor width={layout.width} height={layout.height} />

              <group position={[-layout.width / 2, 0, -layout.height / 2]}>
                <BoundsWrapper>
                  {layout.shelves.map(shelf => <ContextAwareShelf key={shelf.id} shelf={shelf} />)}
                </BoundsWrapper>

                {layers}

                <Bretter />
                <CashRegister />

                {(isDevMode || isIntegrationMode) && selectedShelfId && <FacingsDebug />}

                <AxisLabels3D />
              </group>
            </>
          )}

          <TestBridge />
        </group>
      </ShelfRegistry>
    </Canvas>
  )
}

interface FloorProps {
  width: number
  height: number
}

function Floor({ width, height }: FloorProps) {
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.03, 0]}
      receiveShadow={true}
    >
      <planeGeometry args={[width * 1.1, height * 1.1]} />
      <meshStandardMaterial
        color="#fff"
        roughness={0.6}
        metalness={0.0}
        side={THREE.DoubleSide}
      />
    </mesh>
  )
}

function Loader() {
  return <SpinningUbicaCube />
}

export default ThreeDim